/**
 * Smart Context Builder
 * Builds token-efficient context for AI advisor based on question classification
 *
 * Instead of sending all campaign data, this service:
 * 1. Classifies the user's question
 * 2. Determines what context sections are needed
 * 3. Builds a focused context object with only relevant data
 * 4. Significantly reduces token usage while improving response quality
 */

import {
    classifyQuestion,
    isHighStakesQuestion,
    getSuggestedFollowUps,
    QUESTION_TYPES
} from './questionClassifier';

import {
    buildCampaignKnowledgeBase,
    getKnowledgeSection,
    summarizeCampaign,
    FUNNEL_STAGES
} from './campaignKnowledgeService';

import {
    evaluateCampaignHealth,
    extractMetricValue,
    formatCurrency
} from '../utils/objectiveHelpers';

// ===== Context Size Limits =====

const CONTEXT_LIMITS = {
    // Maximum campaigns to include in detail
    MAX_DETAILED_CAMPAIGNS: 5,
    // Maximum campaigns to include as references
    MAX_CAMPAIGN_REFS: 10,
    // Maximum insights to include
    MAX_INSIGHTS: 5,
    // Maximum issues/opportunities to include
    MAX_ISSUES: 3
};

// ===== Helper Functions =====

/**
 * Get nested value from object using dot notation
 */
const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
            current = current[part];
        } else {
            return undefined;
        }
    }

    return current;
};

/**
 * Safely set nested value in object using dot notation
 */
const setNestedValue = (obj, path, value) => {
    const parts = path.split('.');
    let current = obj;

    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!(part in current)) {
            current[part] = {};
        }
        current = current[part];
    }

    current[parts[parts.length - 1]] = value;
};

// ===== Overall Health Summary =====

/**
 * Create a compact overall health summary
 *
 * IMPORTANT: Now provides THREE distinct data sets clearly labeled:
 * 1. allTimeSpend: Lifetime total spend since campaign creation
 * 2. periodSpend: What was actually spent in the selected date range
 * 3. dailyBudget: Current daily budget allocation (what's scheduled)
 *
 * This distinction prevents the AI from recommending budget reallocation from
 * campaigns that have active budgets but haven't spent yet (new or reactivated campaigns).
 */
const summarizeOverallHealth = (aggregateMetrics, campaigns) => {
    if (!aggregateMetrics) {
        return {
            status: 'unknown',
            message: 'No metrics data available'
        };
    }

    const activeCampaigns = campaigns?.filter(c => c.status?.toUpperCase() === 'ACTIVE') || [];
    const healthScores = activeCampaigns.map(c => evaluateCampaignHealth(c).score);
    const avgHealth = healthScores.length > 0
        ? healthScores.reduce((a, b) => a + b, 0) / healthScores.length
        : 0;

    // DATA SET 1: All-Time (Lifetime total)
    const totalAllTimeSpend = campaigns?.reduce((sum, c) => {
        const m = c.allTimeMetrics || {};
        return sum + (extractMetricValue(m, 'spend') || 0);
    }, 0) || 0;

    const totalAllTimeRevenue = campaigns?.reduce((sum, c) => {
        const m = c.allTimeMetrics || {};
        return sum + (extractMetricValue(m, 'revenue') || 0);
    }, 0) || 0;

    // DATA SET 2: Selected Period (Date range)
    const periodSpend = aggregateMetrics.spend || 0;
    const periodRevenue = aggregateMetrics.revenue || 0;
    const periodRoas = periodSpend > 0 ? periodRevenue / periodSpend : 0;

    // DATA SET 3: Current Daily Budget (Scheduled allocation)
    const totalDailyBudget = activeCampaigns.reduce((sum, c) => {
        return sum + (c.budget?.daily || c.dailyBudget || c.daily_budget || 0);
    }, 0);

    // Count campaigns with $0 period spend (inactive in this period)
    const zeroSpendCampaigns = activeCampaigns.filter(c => {
        const metrics = c.currentMetrics || c.insights?.data?.[0] || {};
        const campaignPeriodSpend = extractMetricValue(metrics, 'spend') || 0;
        return campaignPeriodSpend === 0;
    });

    let status;
    if (avgHealth >= 75) status = 'healthy';
    else if (avgHealth >= 50) status = 'needs_attention';
    else if (avgHealth > 0) status = 'critical';
    else status = 'unknown';

    return {
        status,
        activeCampaigns: activeCampaigns.length,
        avgHealthScore: Math.round(avgHealth),

        // ======= THREE DISTINCT DATA SETS =======

        // DATA SET 1: ALL-TIME (Lifetime total since campaign creation)
        allTime: {
            spend: formatCurrency(totalAllTimeSpend),
            revenue: formatCurrency(totalAllTimeRevenue),
            roas: totalAllTimeSpend > 0 ? (totalAllTimeRevenue / totalAllTimeSpend).toFixed(2) + 'x' : 'N/A',
            _label: 'LIFETIME TOTAL - All spend/revenue since campaign creation'
        },

        // DATA SET 2: SELECTED PERIOD (Within selected date range only)
        selectedPeriod: {
            spend: formatCurrency(periodSpend),
            revenue: formatCurrency(periodRevenue),
            roas: periodRoas.toFixed(2) + 'x',
            _label: 'SELECTED PERIOD - Spend/revenue within the selected date range only'
        },

        // DATA SET 3: CURRENT ALLOCATION (Scheduled/budgeted spend per day)
        currentAllocation: {
            dailyBudget: formatCurrency(totalDailyBudget),
            _label: 'CURRENT ALLOCATION - What is scheduled/budgeted per day (not historical)'
        },

        // ======= LEGACY FIELDS (for backwards compatibility) =======
        totalPeriodSpend: formatCurrency(periodSpend),
        totalSpend: formatCurrency(periodSpend),
        totalDailyBudget: formatCurrency(totalDailyBudget),
        totalRevenue: formatCurrency(periodRevenue),
        overallRoas: periodRoas.toFixed(2) + 'x',

        // ======= ZERO SPEND CAMPAIGNS =======
        // IMPORTANT: These campaigns have $0 spend in the selected period
        // They may be inactive or just started - check allTime for lifetime total
        zeroSpendCampaignsCount: zeroSpendCampaigns.length,
        zeroSpendCampaigns: zeroSpendCampaigns.slice(0, 3).map(c => {
            const allTimeMetrics = c.allTimeMetrics || {};
            return {
                name: c.name,
                allTimeSpend: formatCurrency(extractMetricValue(allTimeMetrics, 'spend') || 0),
                periodSpend: '$0.00',
                dailyBudget: formatCurrency(c.budget?.daily || c.dailyBudget || c.daily_budget || 0),
                spendStatus: 'inactive_zero_spend',
                note: 'This campaign has $0 spend in the selected period - check allTimeSpend for lifetime total'
            };
        })
    };
};

// ===== Context Builders for Specific Question Types =====

/**
 * Build context for funnel-related questions
 *
 * Now includes all THREE data sets per funnel stage:
 * 1. allTimeSpend - Lifetime total since campaign creation
 * 2. periodSpend - What was spent in the selected date range
 * 3. dailyBudget - Current daily budget allocation
 */
const buildFunnelContext = (knowledgeBase, campaigns) => {
    if (knowledgeBase.isEmpty) {
        return {
            hasFunnel: false,
            message: 'No campaign data to analyze funnel structure.'
        };
    }

    const { funnel } = knowledgeBase;

    // Helper to format stage data with all 3 data sets
    const formatStageData = (stage) => ({
        count: stage.count,
        health: stage.health,
        campaigns: stage.campaigns.slice(0, 3).map(c => c.name),

        // ======= THREE DISTINCT DATA SETS =======

        // DATA SET 1: ALL-TIME (Lifetime total)
        allTimeSpend: formatCurrency(stage.metrics.allTimeSpend || 0),

        // DATA SET 2: SELECTED PERIOD (Date range)
        periodSpend: formatCurrency(stage.metrics.spend),
        // Legacy field
        spend: formatCurrency(stage.metrics.spend),

        // DATA SET 3: CURRENT ALLOCATION (Budget)
        dailyBudget: formatCurrency(stage.metrics.totalDailyBudget || 0),

        // ROAS if available
        periodRoas: stage.metrics.roas ? stage.metrics.roas.toFixed(2) + 'x' : null,
        allTimeRoas: stage.metrics.allTimeRoas ? stage.metrics.allTimeRoas.toFixed(2) + 'x' : null
    });

    return {
        hasFunnel: true,
        structure: {
            tofu: formatStageData(funnel.structure.tofu),
            mofu: formatStageData(funnel.structure.mofu),
            bofu: formatStageData(funnel.structure.bofu)
        },
        analysis: {
            summary: funnel.analysis.summary,
            isHealthy: funnel.analysis.isHealthy,
            // Period spend distribution (historical)
            spendDistribution: funnel.analysis.spendDistribution,
            // Daily budget distribution (current allocation)
            budgetDistribution: funnel.analysis.budgetDistribution,
            totalDailyBudget: funnel.analysis.totalDailyBudget
                ? formatCurrency(funnel.analysis.totalDailyBudget)
                : null,
            issues: funnel.analysis.issues.slice(0, CONTEXT_LIMITS.MAX_ISSUES),
            opportunities: funnel.analysis.opportunities.slice(0, 2)
        },
        relationships: funnel.relationships.filter(r =>
            r.type === 'RETARGETING_DEPENDENCY' || r.type === 'FUNNEL_STRUCTURE'
        ),
        // Reminder about data sets
        _dataSetLabels: {
            allTimeSpend: 'LIFETIME TOTAL - All spend since campaign creation',
            periodSpend: 'SELECTED PERIOD - Spend within the selected date range only',
            dailyBudget: 'CURRENT ALLOCATION - What is scheduled/budgeted per day'
        }
    };
};

/**
 * Build context for performance/ROAS questions
 */
const buildPerformanceContext = (knowledgeBase, aggregateMetrics, trends) => {
    if (knowledgeBase.isEmpty) {
        return {
            hasData: false,
            message: 'No performance data available.'
        };
    }

    const { performanceByStage, budgetAnalysis } = knowledgeBase;

    return {
        hasData: true,
        byStage: {
            awareness: {
                count: performanceByStage.awareness.count,
                spend: formatCurrency(performanceByStage.awareness.spend),
                health: performanceByStage.awareness.health
            },
            consideration: {
                count: performanceByStage.consideration.count,
                spend: formatCurrency(performanceByStage.consideration.spend),
                health: performanceByStage.consideration.health
            },
            conversion: {
                count: performanceByStage.conversion.count,
                spend: formatCurrency(performanceByStage.conversion.spend),
                roas: performanceByStage.conversion.roas + 'x',
                health: performanceByStage.conversion.health
            }
        },
        trends: trends ? {
            spend: trends.spend?.formatted || 'N/A',
            revenue: trends.revenue?.formatted || 'N/A',
            roas: trends.roas?.formatted || 'N/A'
        } : null,
        recommendations: budgetAnalysis.recommendations?.slice(0, 2) || []
    };
};

/**
 * Build context for budget-related questions
 *
 * IMPORTANT: Now includes all THREE data sets clearly labeled:
 * 1. allTimeSpend: Lifetime total spend since campaign creation
 * 2. periodSpend: Amount spent during the selected date range
 * 3. dailyBudget: Current daily budget allocation (what's scheduled)
 */
const buildBudgetContext = (knowledgeBase) => {
    if (knowledgeBase.isEmpty) {
        return {
            hasData: false,
            message: 'No budget data available.'
        };
    }

    const { budgetAnalysis, funnel } = knowledgeBase;

    return {
        hasData: true,

        // ======= THREE DISTINCT TOTALS =======

        // DATA SET 1: ALL-TIME SPEND (Lifetime total)
        totalAllTimeSpend: formatCurrency(budgetAnalysis.totalAllTimeSpend || 0),
        totalAllTimeSpend_label: 'LIFETIME TOTAL - All spend since campaign creation',

        // DATA SET 2: SELECTED PERIOD SPEND (Date range)
        totalPeriodSpend: formatCurrency(budgetAnalysis.totalPeriodSpend || 0),
        totalPeriodSpend_label: 'SELECTED PERIOD - Spend within the selected date range only',

        // DATA SET 3: CURRENT DAILY BUDGET (Scheduled allocation)
        totalDailyBudget: formatCurrency(budgetAnalysis.totalDailyBudget || 0),
        totalDailyBudget_label: 'CURRENT ALLOCATION - What is scheduled/budgeted per day',

        // ======= DISTRIBUTION BY FUNNEL STAGE =======

        // All-time spend by stage
        allTimeSpendByStage: budgetAnalysis.allTimeSpendByStage || null,
        // Period spend by stage
        periodSpendByStage: budgetAnalysis.periodSpendByStage || null,
        // Daily budget by stage
        budgetByStage: budgetAnalysis.budgetByStage || null,
        // Legacy field
        distribution: budgetAnalysis.distributionPercent,

        // ======= TOP CAMPAIGNS =======

        // Top by all-time spend (lifetime)
        topAllTimeSpenders: budgetAnalysis.topAllTimeSpenders?.slice(0, 3) || [],
        // Top by period spend (selected date range)
        topPeriodSpenders: budgetAnalysis.topPeriodSpenders?.slice(0, 3) || [],
        // Top by daily budget allocation (current)
        topBudgetAllocations: budgetAnalysis.topBudgetAllocations?.slice(0, 3) || [],
        // Legacy alias
        topSpenders: budgetAnalysis.topSpenders?.slice(0, 3) || [],

        // ======= ZERO SPEND CAMPAIGNS =======
        // CRITICAL: These campaigns have $0 spend in the selected period
        // They may be inactive or just started - check allTimeSpend for lifetime total
        zeroSpendCampaigns: budgetAnalysis.zeroSpendCampaigns || [],
        zeroSpendNote: budgetAnalysis.zeroSpendCampaigns?.length > 0
            ? 'These campaigns have $0 spend in the selected period - check allTimeSpend for lifetime total. Do NOT recommend reallocating budget from these campaigns without checking their lifetime spend first.'
            : null,

        recommendations: budgetAnalysis.recommendations,

        // Funnel balance
        funnelSpendBalance: funnel?.analysis?.spendDistribution || null,
        funnelBudgetBalance: funnel?.analysis?.budgetDistribution || null
    };
};

/**
 * Get metrics from multiple possible locations on a campaign object
 */
const getCampaignMetrics = (campaign) => {
    // Check multiple possible locations where metrics might be stored
    return campaign.currentMetrics ||
           campaign.insights?.data?.[0] ||
           campaign.metrics ||
           {};
};

/**
 * Build context for specific campaign questions
 *
 * Now includes all THREE data sets for each campaign:
 * 1. allTime - Lifetime total since campaign creation
 * 2. selectedPeriod - Metrics within the selected date range
 * 3. currentAllocation - Current daily/lifetime budget
 */
const buildCampaignContext = (campaigns, mentionedCampaignIds) => {
    if (!mentionedCampaignIds || mentionedCampaignIds.length === 0) {
        return null;
    }

    const mentionedCampaigns = campaigns.filter(c =>
        mentionedCampaignIds.includes(c.id) || mentionedCampaignIds.includes(c.platformCampaignId)
    );

    return mentionedCampaigns.map(campaign => {
        const periodMetrics = getCampaignMetrics(campaign);
        const allTimeMetrics = campaign.allTimeMetrics || {};
        const health = evaluateCampaignHealth(campaign);

        const periodSpend = extractMetricValue(periodMetrics, 'spend') || 0;
        const allTimeSpend = extractMetricValue(allTimeMetrics, 'spend') || 0;
        const dailyBudget = campaign.budget?.daily || campaign.dailyBudget || campaign.daily_budget || 0;

        return {
            id: campaign.id || campaign.platformCampaignId,
            name: campaign.name,
            status: campaign.status,
            objective: campaign.objective,
            health: health.status,
            healthScore: health.score,
            primaryKPI: health.formattedValue,

            // ======= THREE DISTINCT DATA SETS =======

            // DATA SET 1: ALL-TIME METRICS (Lifetime total)
            allTime: {
                spend: formatCurrency(allTimeSpend),
                revenue: formatCurrency(extractMetricValue(allTimeMetrics, 'revenue') || 0),
                roas: (extractMetricValue(allTimeMetrics, 'roas') || 0).toFixed(2) + 'x',
                conversions: Math.round(extractMetricValue(allTimeMetrics, 'conversions') || 0),
                _label: 'LIFETIME TOTAL - All spend since campaign creation'
            },

            // DATA SET 2: SELECTED PERIOD METRICS (Date range)
            selectedPeriod: {
                spend: formatCurrency(periodSpend),
                revenue: formatCurrency(extractMetricValue(periodMetrics, 'revenue') || 0),
                roas: (extractMetricValue(periodMetrics, 'roas') || 0).toFixed(2) + 'x',
                impressions: (extractMetricValue(periodMetrics, 'impressions') || 0).toLocaleString(),
                clicks: (extractMetricValue(periodMetrics, 'clicks') || 0).toLocaleString(),
                conversions: Math.round(extractMetricValue(periodMetrics, 'conversions') || 0),
                _label: 'SELECTED PERIOD - Spend within the selected date range only'
            },

            // DATA SET 3: CURRENT ALLOCATION (Budget)
            currentAllocation: {
                dailyBudget: formatCurrency(dailyBudget),
                lifetimeBudget: campaign.budget?.lifetime ? formatCurrency(campaign.budget.lifetime) : null,
                _label: 'CURRENT ALLOCATION - What is scheduled/budgeted (not historical)'
            },

            // ======= STATUS FLAGS =======
            hasZeroPeriodSpend: periodSpend === 0,
            spendStatus: periodSpend === 0
                ? 'inactive_zero_spend'
                : (periodSpend < dailyBudget ? 'low_spend' : 'active'),
            spendStatusNote: periodSpend === 0
                ? 'This campaign has $0 spend in the selected period - check allTime.spend for lifetime total'
                : null,

            // ======= LEGACY FIELDS =======
            metrics: {
                spend: formatCurrency(periodSpend),
                revenue: formatCurrency(extractMetricValue(periodMetrics, 'revenue') || 0),
                roas: (extractMetricValue(periodMetrics, 'roas') || 0).toFixed(2) + 'x',
                impressions: (extractMetricValue(periodMetrics, 'impressions') || 0).toLocaleString(),
                clicks: (extractMetricValue(periodMetrics, 'clicks') || 0).toLocaleString(),
                conversions: Math.round(extractMetricValue(periodMetrics, 'conversions') || 0)
            },
            dailyBudget: dailyBudget
        };
    });
};

// ===== Main Smart Context Builder =====

/**
 * Build smart context for AI advisor based on the question
 *
 * @param {string} question - The user's question
 * @param {Object} options - Context building options
 * @param {Array} options.campaigns - Full campaign list
 * @param {Object} options.aggregateMetrics - Aggregate metrics
 * @param {Object} options.previousMetrics - Previous period metrics
 * @param {Object} options.aggregateTrends - Calculated trends
 * @param {Object} options.previousPeriodData - Campaign-level previous data
 * @param {Array} options.insights - Generated insights
 * @param {string} options.dateRangeLabel - Current date range label
 * @param {Object} options.userGoal - User's selected goal
 * @returns {Object} Token-efficient context object
 */
export const buildSmartContext = (question, options = {}) => {
    const {
        campaigns = [],
        aggregateMetrics = null,
        previousMetrics = null,
        aggregateTrends = null,
        previousPeriodData = {},
        insights = [],
        dateRangeLabel = 'this period',
        userGoal = null
    } = options;

    // Step 1: Classify the question
    const classification = classifyQuestion(question, campaigns);

    // Step 2: Build the knowledge base (this is cached in context)
    const knowledgeBase = buildCampaignKnowledgeBase(campaigns, previousPeriodData);

    // Step 3: Build base context (always included)
    const context = {
        // Metadata
        questionType: classification.type,
        questionPriority: classification.priority,
        dateRange: dateRangeLabel,

        // Explicit data availability flags - helps AI know what data exists
        dataAvailability: {
            hasCampaigns: campaigns.length > 0,
            hasActiveCampaigns: campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE').length > 0,
            campaignCount: campaigns.length,
            hasMetrics: !!aggregateMetrics && (aggregateMetrics.spend > 0 || aggregateMetrics.revenue > 0),
            hasPreviousPeriodData: Object.keys(previousPeriodData).length > 0,
            hasInsights: insights.length > 0
        },

        // Overall health summary (compact, ~50 tokens)
        overallHealth: summarizeOverallHealth(aggregateMetrics, campaigns),

        // User's goal if set
        userGoal: userGoal ? {
            objective: userGoal.objective,
            targetMetric: userGoal.targetMetric
        } : null
    };

    // If no campaigns, add explicit warning
    if (campaigns.length === 0) {
        context.warning = 'NO_CAMPAIGN_DATA: There are no campaigns in the current context. Do not invent campaign data.';
    }

    // Step 4: Add required context sections based on question type
    for (const path of classification.requiredContext) {
        const value = getKnowledgeSection(knowledgeBase, path);
        if (value !== null && value !== undefined) {
            // Simplify the path for the context object
            const simplePath = path.split('.').pop();
            setNestedValue(context, path, value);
        }
    }

    // Step 5: Add optional context if it exists and question is high-stakes
    if (isHighStakesQuestion(classification)) {
        for (const path of classification.optionalContext) {
            const value = getKnowledgeSection(knowledgeBase, path);
            if (value !== null && value !== undefined) {
                setNestedValue(context, path, value);
            }
        }
    }

    // Step 6: Add type-specific enriched context
    switch (classification.type) {
        case QUESTION_TYPES.FUNNEL_IMPACT:
        case QUESTION_TYPES.FUNNEL_STRATEGY:
        case QUESTION_TYPES.FUNNEL_BUILD:
            context.funnelContext = buildFunnelContext(knowledgeBase, campaigns);
            break;

        case QUESTION_TYPES.ROAS_IMPROVEMENT:
        case QUESTION_TYPES.CPA_OPTIMIZATION:
        case QUESTION_TYPES.PERFORMANCE_DIAGNOSIS:
            context.performanceContext = buildPerformanceContext(
                knowledgeBase,
                aggregateMetrics,
                aggregateTrends
            );
            break;

        case QUESTION_TYPES.BUDGET_ALLOCATION:
        case QUESTION_TYPES.BUDGET_OPTIMIZATION:
        case QUESTION_TYPES.SCALING:
            context.budgetContext = buildBudgetContext(knowledgeBase);
            context.performanceContext = buildPerformanceContext(
                knowledgeBase,
                aggregateMetrics,
                aggregateTrends
            );
            break;

        case QUESTION_TYPES.PAUSE_DECISION:
        case QUESTION_TYPES.CAMPAIGN_ACTION:
            context.funnelContext = buildFunnelContext(knowledgeBase, campaigns);
            if (classification.mentionedCampaigns.length > 0) {
                context.focusedCampaigns = buildCampaignContext(
                    campaigns,
                    classification.mentionedCampaigns
                );
            }
            break;

        case QUESTION_TYPES.SPECIFIC_CAMPAIGN:
            context.focusedCampaigns = buildCampaignContext(
                campaigns,
                classification.mentionedCampaigns
            );
            break;
    }

    // Step 7: Add relevant insights (limited)
    if (insights.length > 0) {
        context.topInsights = insights
            .slice(0, CONTEXT_LIMITS.MAX_INSIGHTS)
            .map(insight => ({
                type: insight.type,
                priority: insight.priority,
                title: insight.title,
                campaignName: campaigns.find(c => c.id === insight.campaignId)?.name,
                action: insight.action
            }));
    }

    // Step 8: Add suggested follow-ups for the AI to offer
    context.suggestedFollowUps = getSuggestedFollowUps(classification.type);

    return context;
};

/**
 * Build a minimal context for general questions (lowest token usage)
 */
export const buildMinimalContext = (options = {}) => {
    const {
        aggregateMetrics = null,
        campaigns = [],
        dateRangeLabel = 'this period'
    } = options;

    const activeCampaigns = campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');

    return {
        dateRange: dateRangeLabel,
        summary: {
            totalCampaigns: campaigns.length,
            activeCampaigns: activeCampaigns.length,
            totalSpend: formatCurrency(aggregateMetrics?.spend || 0),
            totalRevenue: formatCurrency(aggregateMetrics?.revenue || 0),
            overallRoas: aggregateMetrics?.spend > 0
                ? ((aggregateMetrics.revenue || 0) / aggregateMetrics.spend).toFixed(2) + 'x'
                : 'N/A'
        }
    };
};

/**
 * Estimate token count for a context object
 * Rough estimation: 1 token ≈ 4 characters for English text
 */
export const estimateTokenCount = (context) => {
    const jsonString = JSON.stringify(context);
    return Math.ceil(jsonString.length / 4);
};

/**
 * Compare context sizes between full and smart context
 */
export const compareContextSizes = (fullContext, smartContext) => {
    const fullTokens = estimateTokenCount(fullContext);
    const smartTokens = estimateTokenCount(smartContext);
    const savings = fullTokens - smartTokens;
    const savingsPercent = ((savings / fullTokens) * 100).toFixed(1);

    return {
        fullContextTokens: fullTokens,
        smartContextTokens: smartTokens,
        tokensSaved: savings,
        percentSaved: savingsPercent + '%'
    };
};

export default {
    buildSmartContext,
    buildMinimalContext,
    estimateTokenCount,
    compareContextSizes
};
